import gsap from 'gsap'
import {
  Color,
  Euler,
  Group,
  Matrix4,
  Mesh,
  PlaneGeometry,
  Quaternion,
  ShaderMaterial,
  TextureLoader,
  Vector3,
  Vector4,
} from 'three'

import securityImgUrl from '../../../assets/images/brandedDivider/security.png'
import type {BrandedDividerParams} from '../../../components/WebGLContext/WebGLContext.types'
import {bgColor} from '../../_config'
import fragmentShader from '../../_shaders/cube/securityCube'
import frustumCull from '../../_shaders/frustumCull'
import vertexShader from '../../_shaders/vertexShader'
import securityParams from '../../brandedDivider/characters/security'
import {PARALLAX_ROTATION} from './_config'
import RoundedCubeGeometry from './RoundedCubeGeometry'

export default class SecurityCube {
  _cubeGeometry: RoundedCubeGeometry
  params: BrandedDividerParams
  group: Group = new Group()
  shieldGroup: Group = new Group()
  cubeGroup: Group = new Group()
  rectData: Vector4
  parentGroup: Group | null = null
  tmpQuaternion: Quaternion = new Quaternion()
  tmpEuler: Euler = new Euler()

  _rotationParam: Vector3 = new Vector3()
  constructor(params: BrandedDividerParams, _cubeGeometry: RoundedCubeGeometry, rectData: Vector4) {
    this.params = params
    this._cubeGeometry = _cubeGeometry
    this.rectData = rectData
    this.group.add(this.shieldGroup)
    this.group.add(this.cubeGroup)
  }
  async create(parentGroup: Group, tl: gsap.core.Timeline) {
    this.parentGroup = parentGroup
    parentGroup.add(this.group)
    if (this.params.character !== 'security') return

    const params = securityParams[this.params.layout]
    if (!params) return

    this.group.scale.set(params.scale, params.scale, params.scale)

    const progress = new Vector4(0, 0, 0, 0)

    const textureLoader = new TextureLoader()
    const texture = await textureLoader.loadAsync(securityImgUrl)
    const aspect = texture.image.width / texture.image.height

    const shieldMesh = new Mesh(
      new PlaneGeometry(aspect * 1.3, 1.3),
      new ShaderMaterial({
        vertexShader: `
        varying vec2 vUv;
        void main(){
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
        `,
        fragmentShader: `
        varying vec2 vUv;
        uniform sampler2D uTexture;
        uniform vec4 uRectData;
        uniform vec4 uProgress;

        void main(){
          vec4 color = texture2D(uTexture, vUv);
          ${frustumCull}

          color.rgb /= color.a;
          gl_FragColor = color;
          gl_FragColor.a *= uProgress.x;
        }
        `,
        uniforms: {
          uTexture: {value: texture},
          uRectData: {value: this.rectData},
          uProgress: {value: progress},
        },
        transparent: true,
      }),
    )
    shieldMesh.position.set(0, 0, 1.2)
    shieldMesh.name = 'shield'

    this.shieldGroup.add(shieldMesh)

    const cubeMesh = new Mesh(
      this._cubeGeometry,
      new ShaderMaterial({
        vertexShader,
        fragmentShader,
        uniforms: {
          uColor1: {
            value: new Color(0xccedff),
          },
          uRectData: {value: this.rectData},
          uProgress: {
            value: progress,
          },
          uBgColor: {
            value: bgColor,
          },
        },
        defines: {
          MOSAIC: '',
        },
      }),
    )

    this.cubeGroup.add(cubeMesh)

    cubeMesh.rotation.order = 'ZXY'
    cubeMesh.name = 'securityCube'

    cubeMesh.userData.position = {
      def: params.position.def.clone(),
      offset: params.position.offset.clone(),
      current: params.position.current.clone(),
    }

    cubeMesh.userData.rotation = {
      def: new Vector3(0, 0, 0),
      offset: params.rotation.offset.clone(),
      defMatrix: new Matrix4(),
      offsetMatrix: new Matrix4(),
      currentMatrix: new Matrix4(),
      defEuler: new Euler(),
      offsetEuler: new Euler(),
    }

    this._rotationParam.copy(params.rotation.def)

    const duration = 1.5
    const delay = 0.0

    tl.to(
      cubeMesh.userData.position.offset,
      {
        x: 0,
        y: 0,
        z: 0,
        ease: params.position.ease,
        duration: params.position.duration,
      },
      delay,
    )
      .to(
        cubeMesh.userData.rotation.offset,
        {
          x: 0,
          y: 0,
          z: 0,
          ease: params.rotation.ease,
          duration: params.rotation.duration,
        },
        delay,
      )
      .to(
        progress,
        {
          x: 1,
          ease: 'expo.out',
          duration: 1.0,
        },
        delay,
      )
      .to(
        progress,
        {
          y: 1,
          ease: 'expo.out',
          duration: 3.0,
        },
        delay,
      )
  }

  update(scrollProgress: number) {
    const cubeMesh = this.group.getObjectByName('securityCube') as Mesh

    if (!cubeMesh) return

    const position = cubeMesh.userData.position
    const rotation = cubeMesh.userData.rotation

    position.current.copy(position.def).add(position.offset)
    this.group.position.copy(position.current)
    // Apply rotations using matrix composition: rotation.def * rotation.offset
    rotation.defEuler.set(rotation.def.x, rotation.def.y, rotation.def.z, 'ZXY')
    rotation.offsetEuler.set(rotation.offset.x, rotation.offset.y, rotation.offset.z, 'ZXY')
    rotation.defMatrix.makeRotationFromEuler(rotation.defEuler)
    rotation.offsetMatrix.makeRotationFromEuler(rotation.offsetEuler)

    // Compose rotations: first apply def rotation, then offset rotation
    rotation.currentMatrix.multiplyMatrices(rotation.defMatrix, rotation.offsetMatrix)

    // Extract the final rotation back to Euler and apply to mesh
    cubeMesh.rotation.setFromRotationMatrix(rotation.currentMatrix)

    this.updateObjects(scrollProgress)
  }

  updateObjects(scrollProgress: number) {
    const shield = this.group.getObjectByName('shield') as Mesh
    if (!shield || !this.parentGroup) return

    const invertQ = this.tmpQuaternion.copy(this.parentGroup.quaternion).invert()
    this.shieldGroup.quaternion.copy(invertQ)

    this.tmpEuler.set(this._rotationParam.x, this._rotationParam.y, this._rotationParam.z, 'YXZ')

    if (this.params.layout === 0) {
      this.tmpEuler.x -= scrollProgress * Math.PI * PARALLAX_ROTATION
      shield.rotation.z = 0.6 - scrollProgress * Math.PI * PARALLAX_ROTATION
      this.group.position.y += scrollProgress * 0.1

      this.cubeGroup.quaternion.copy(invertQ)
    }

    if (this.params.layout === 1) {
      shield.rotation.z = 0.6 - scrollProgress * Math.PI * PARALLAX_ROTATION
    }

    this.tmpQuaternion.setFromEuler(this.tmpEuler)
    this.cubeGroup.quaternion.multiply(this.tmpQuaternion)
  }
}
